import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminGroupsAPI, validationAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

interface AdminGroup {
  id: number;
  name: string;
  description: string;
  admin_manages_timesheets?: boolean;
  employee_count: number;
}

interface Employee {
  id: number;
  nome: string;
  cognome: string;
  matricola?: string | null;
}

interface PresenceValidation {
  id: number;
  employee_id: number;
  employee_name?: string;
  employee_surname?: string;
  employee_matricola?: string | null;
  year: number;
  month: number;
  total_presence_hours: number;
  total_leave_hours: number;
  total_permission_hours: number;
  total_other_absence_hours: number;
  total_working_days: number;
  validated_at: string;
  validated_by: number;
  validator_name: string;
  validator_surname: string;
  validation_notes?: string | null;
  is_provisional?: boolean;
  validation_level?: string;
  has_anomalies?: boolean;
  group_id?: number | null;
  group_name?: string | null;
}

const monthNames = [
  '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
];

const TimesheetManagement: React.FC = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<AdminGroup[]>([]);
  const [employeesWithoutGroup, setEmployeesWithoutGroup] = useState<Employee[]>([]);
  const [validations, setValidations] = useState<PresenceValidation[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Filtri
  const [selectedGroupId, setSelectedGroupId] = useState<number | 'without-group' | 'all'>('all');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number | 'all'>('all'); // Cambiato da mese corrente a 'all'
  const [filterAnomalies, setFilterAnomalies] = useState<'all' | 'with' | 'without'>('with'); // Cambiato default a 'with'
  const [selectedValidations, setSelectedValidations] = useState<number[]>([]);

  // Carica gruppi
  const loadGroups = async () => {
    try {
      if (isSuperAdmin) {
        const response = await adminGroupsAPI.getAllGroups();
        setGroups(response.groups || []);
      } else {
        const response = await adminGroupsAPI.getMyGroups();
        setGroups(response.groups || []);
      }
    } catch (err: any) {
      // Errore caricamento gruppi
    }
  };

  // Carica dipendenti senza gruppo (solo super admin)
  const loadEmployeesWithoutGroup = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await adminGroupsAPI.getEmployeesWithoutGroup();
      setEmployeesWithoutGroup(response.employees || []);
    } catch (err: any) {
      // Errore caricamento dipendenti senza gruppo
    }
  };

  // Carica validazioni - carica TUTTE le validazioni (non filtrare per has_anomalies)
  const loadValidations = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: any = {
        year: selectedYear,
      };

      if (selectedMonth !== 'all') {
        filters.month = selectedMonth;
      }

      // NON filtrare per has_anomalies qui - vogliamo vedere tutte le validazioni
      // Il filtro per anomalie verrà applicato nel groupedValidations

      // Filtro per gruppo (solo super admin può filtrare per gruppo specifico)
      if (isSuperAdmin && selectedGroupId !== 'all' && selectedGroupId !== 'without-group') {
        filters.group_id = selectedGroupId;
      }

      const response = await validationAPI.getValidationsForAdmin(filters);
      let validationsList: PresenceValidation[] = response.validations || [];

      // Se super admin e selezionato "without-group", filtra manualmente
      if (isSuperAdmin && selectedGroupId === 'without-group') {
        const employeeIdsWithoutGroup = employeesWithoutGroup.map(e => e.id);
        validationsList = validationsList.filter(v => 
          employeeIdsWithoutGroup.includes(v.employee_id)
        );
      }

      setValidations(validationsList);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento validazioni');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    if (isSuperAdmin) {
      loadEmployeesWithoutGroup();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    loadValidations();
  }, [selectedGroupId, selectedYear, selectedMonth, filterAnomalies]);

  // Genera anni disponibili (ultimi 3 anni)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => currentYear - i);
  }, []);

  // Gestione selezione multipla
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedValidations(validations.map(v => v.id));
    } else {
      setSelectedValidations([]);
    }
  };

  const handleSelectValidation = (validationId: number) => {
    setSelectedValidations(prev => 
      prev.includes(validationId)
        ? prev.filter(id => id !== validationId)
        : [...prev, validationId]
    );
  };

  // Download massivo
  const handleBulkDownload = async () => {
    if (selectedValidations.length === 0) {
      alert('Seleziona almeno un cartellino da scaricare');
      return;
    }

    // TODO: Implementare download massivo
    alert(`Download di ${selectedValidations.length} cartellini (da implementare)`);
  };

  // Naviga alla pagina di correzione anomalie
  const handleFixAnomalies = (validation: PresenceValidation) => {
    // Naviga alla pagina di validazione presenze con i parametri del dipendente
    navigate(`/presence-validation?user_id=${validation.employee_id}&year=${validation.year}&month=${validation.month}`);
  };

  // Visualizza dettagli cartellino
  const handleViewDetails = (validation: PresenceValidation) => {
    navigate(`/presence-validation?user_id=${validation.employee_id}&year=${validation.year}&month=${validation.month}`);
  };

  // Raggruppa validazioni con anomalie per gruppo
  const groupedValidations = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    // Filtra solo quelle con anomalie pregresse (mese < mese corrente)
    const withAnomalies = validations.filter(v => {
      const hasAnomaly = v.has_anomalies === true;
      const isPastMonth = v.year < currentYear || (v.year === currentYear && v.month < currentMonth);
      return hasAnomaly && isPastMonth;
    });

    // Raggruppa per employee_id - mostra solo l'ultima validazione con anomalie per ogni operatore
    const employeeValidationsMap = new Map<number, PresenceValidation>();
    
    // Per ogni operatore, prendi l'ultima validazione con anomalie (più recente)
    withAnomalies.forEach(v => {
      const existing = employeeValidationsMap.get(v.employee_id);
      if (!existing || 
          v.year > existing.year || 
          (v.year === existing.year && v.month > existing.month)) {
        employeeValidationsMap.set(v.employee_id, v);
      }
    });

    const uniqueValidations = Array.from(employeeValidationsMap.values());

    // Raggruppa per gruppo
    const grouped: { [key: string]: PresenceValidation[] } = {};
    const withoutGroup: PresenceValidation[] = [];

    uniqueValidations.forEach(v => {
      if (v.group_id && v.group_name) {
        const key = `${v.group_id}_${v.group_name}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(v);
      } else {
        withoutGroup.push(v);
      }
    });

    return { grouped, withoutGroup };
  }, [validations]);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0d47a1', mb: 1 }}>
            Gestione Cartellini
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visualizza e gestisci i cartellini dei dipendenti, correggi anomalie pregresse e scarica i cartellini validati.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadValidations}
          disabled={loading}
        >
          Aggiorna
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          <FilterListIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Filtri
        </Typography>
        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Gruppo</InputLabel>
                <Select
                  value={selectedGroupId}
                  label="Gruppo"
                  onChange={(e) => setSelectedGroupId(e.target.value as any)}
                >
                  <MenuItem value="all">Tutti i gruppi</MenuItem>
                  {groups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name} ({group.employee_count} dipendenti)
                    </MenuItem>
                  ))}
                  <MenuItem value="without-group">
                    Senza gruppo ({employeesWithoutGroup.length} dipendenti)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {!isSuperAdmin && groups.length > 0 && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Gruppo</InputLabel>
                <Select
                  value={selectedGroupId}
                  label="Gruppo"
                  onChange={(e) => setSelectedGroupId(e.target.value as any)}
                >
                  <MenuItem value="all">Tutti i miei gruppi</MenuItem>
                  {groups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name} ({group.employee_count} dipendenti)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Anno</InputLabel>
              <Select
                value={selectedYear}
                label="Anno"
                onChange={(e) => setSelectedYear(e.target.value as number)}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Mese</InputLabel>
              <Select
                value={selectedMonth}
                label="Mese"
                onChange={(e) => setSelectedMonth(e.target.value as any)}
              >
                <MenuItem value="all">Tutti i mesi</MenuItem>
                {monthNames.slice(1).map((name, index) => (
                  <MenuItem key={index + 1} value={index + 1}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Anomalie</InputLabel>
              <Select
                value={filterAnomalies}
                label="Anomalie"
                onChange={(e) => setFilterAnomalies(e.target.value as any)}
              >
                <MenuItem value="all">Tutte</MenuItem>
                <MenuItem value="with">Con anomalie pregresse</MenuItem>
                <MenuItem value="without">Senza anomalie</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Statistiche */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Cartellini Totali
              </Typography>
              <Typography variant="h4">
                {validations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Con Anomalie Pregresse
              </Typography>
              <Typography variant="h4" color="error">
                {validations.filter(v => v.has_anomalies && (
                  v.year < new Date().getFullYear() || 
                  (v.year === new Date().getFullYear() && v.month < new Date().getMonth() + 1)
                )).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Selezionati
              </Typography>
              <Typography variant="h4">
                {selectedValidations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Elenco operatori con anomalie raggruppati per gruppo */}
      <Paper>
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6">
            Operatori con Anomalie Pregresse
          </Typography>
          {selectedValidations.length > 0 && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleBulkDownload}
            >
              Scarica Selezionati ({selectedValidations.length})
            </Button>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : Object.keys(groupedValidations.grouped).length === 0 && groupedValidations.withoutGroup.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">
              Nessun operatore con anomalie pregresse trovato
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Accordion per ogni gruppo */}
            {Object.entries(groupedValidations.grouped).map(([key, groupValidations]) => {
              const [groupId, groupName] = key.split('_', 2);
              return (
                <Accordion key={key} defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                      <Typography variant="h6">
                        {groupName}
                      </Typography>
                      <Chip 
                        label={`${groupValidations.length} operatore${groupValidations.length !== 1 ? 'i' : ''}`}
                        color="error"
                        size="small"
                        icon={<WarningIcon />}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={groupValidations.every(v => selectedValidations.includes(v.id))}
                                indeterminate={
                                  groupValidations.some(v => selectedValidations.includes(v.id)) &&
                                  !groupValidations.every(v => selectedValidations.includes(v.id))
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    groupValidations.forEach(v => {
                                      if (!selectedValidations.includes(v.id)) {
                                        setSelectedValidations(prev => [...prev, v.id]);
                                      }
                                    });
                                  } else {
                                    setSelectedValidations(prev => 
                                      prev.filter(id => !groupValidations.some(v => v.id === id))
                                    );
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell>Dipendente</TableCell>
                            <TableCell>Periodo</TableCell>
                            <TableCell align="right">Ore Presenza</TableCell>
                            <TableCell align="right">Ore Ferie</TableCell>
                            <TableCell align="right">Ore Permesso</TableCell>
                            <TableCell align="right">Giorni Lavorativi</TableCell>
                            <TableCell>Validato il</TableCell>
                            <TableCell align="center">Azioni</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupValidations.map((validation) => (
                            <TableRow key={validation.id} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedValidations.includes(validation.id)}
                                  onChange={() => handleSelectValidation(validation.id)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {validation.employee_name} {validation.employee_surname}
                                </Typography>
                                {validation.employee_matricola && (
                                  <Typography variant="caption" color="text.secondary">
                                    Matr: {validation.employee_matricola}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {monthNames[validation.month]} {validation.year}
                              </TableCell>
                              <TableCell align="right">
                                {validation.total_presence_hours.toFixed(2)}h
                              </TableCell>
                              <TableCell align="right">
                                {validation.total_leave_hours.toFixed(2)}h
                              </TableCell>
                              <TableCell align="right">
                                {validation.total_permission_hours.toFixed(2)}h
                              </TableCell>
                              <TableCell align="right">
                                {validation.total_working_days}
                              </TableCell>
                              <TableCell>
                                {new Date(validation.validated_at).toLocaleDateString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                })}
                              </TableCell>
                              <TableCell align="center">
                                <Stack direction="row" spacing={1} justifyContent="center">
                                  <Tooltip title="Visualizza dettagli">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewDetails(validation)}
                                    >
                                      <VisibilityIcon />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Correggi anomalie">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleFixAnomalies(validation)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Dipendenti senza gruppo */}
            {groupedValidations.withoutGroup.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                    <Typography variant="h6">
                      Senza Gruppo
                    </Typography>
                    <Chip 
                      label={`${groupedValidations.withoutGroup.length} operatore${groupedValidations.withoutGroup.length !== 1 ? 'i' : ''}`}
                      color="error"
                      size="small"
                      icon={<WarningIcon />}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={groupedValidations.withoutGroup.every(v => selectedValidations.includes(v.id))}
                              indeterminate={
                                groupedValidations.withoutGroup.some(v => selectedValidations.includes(v.id)) &&
                                !groupedValidations.withoutGroup.every(v => selectedValidations.includes(v.id))
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  groupedValidations.withoutGroup.forEach(v => {
                                    if (!selectedValidations.includes(v.id)) {
                                      setSelectedValidations(prev => [...prev, v.id]);
                                    }
                                  });
                                } else {
                                  setSelectedValidations(prev => 
                                    prev.filter(id => !groupedValidations.withoutGroup.some(v => v.id === id))
                                  );
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>Dipendente</TableCell>
                          <TableCell>Periodo</TableCell>
                          <TableCell align="right">Ore Presenza</TableCell>
                          <TableCell align="right">Ore Ferie</TableCell>
                          <TableCell align="right">Ore Permesso</TableCell>
                          <TableCell align="right">Giorni Lavorativi</TableCell>
                          <TableCell>Validato il</TableCell>
                          <TableCell align="center">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupedValidations.withoutGroup.map((validation) => (
                          <TableRow key={validation.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedValidations.includes(validation.id)}
                                onChange={() => handleSelectValidation(validation.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {validation.employee_name} {validation.employee_surname}
                              </Typography>
                              {validation.employee_matricola && (
                                <Typography variant="caption" color="text.secondary">
                                  Matr: {validation.employee_matricola}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {monthNames[validation.month]} {validation.year}
                            </TableCell>
                            <TableCell align="right">
                              {validation.total_presence_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="right">
                              {validation.total_leave_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="right">
                              {validation.total_permission_hours.toFixed(2)}h
                            </TableCell>
                            <TableCell align="right">
                              {validation.total_working_days}
                            </TableCell>
                            <TableCell>
                              {new Date(validation.validated_at).toLocaleDateString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </TableCell>
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <Tooltip title="Visualizza dettagli">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDetails(validation)}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Correggi anomalie">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleFixAnomalies(validation)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </Paper>

      {/* Sezione Validazione Batch */}
      <BatchValidationSection
        year={selectedYear}
        month={typeof selectedMonth === 'number' ? selectedMonth : undefined}
        onValidated={loadValidations}
      />
    </Container>
  );
};

// Componente Validazione Batch
interface BatchValidationSectionProps {
  year: number;
  month?: number;
  onValidated: () => void;
}

interface PendingEmployee {
  employee_id: number;
  user_id: number | null;
  nome: string;
  cognome: string;
  matricola: string | null;
  anomaly_status: 'clean' | 'minor' | 'significant';
  anomalies_count: number;
  can_validate: boolean;
}

const BatchValidationSection: React.FC<BatchValidationSectionProps> = ({ year, month, onValidated }) => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [pendingEmployees, setPendingEmployees] = useState<PendingEmployee[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadPending = async () => {
    if (!month) return;
    setLoading(true);
    setError(null);
    try {
      const data = await validationAPI.getPendingValidations(year, month);
      setPendingEmployees(data.employees || []);
      setSelectedIds([]);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (month) loadPending();
  }, [year, month]);

  const validatableEmployees = pendingEmployees.filter(e => e.can_validate);
  const allSelected = validatableEmployees.length > 0 && validatableEmployees.every(e => selectedIds.includes(e.employee_id));

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(validatableEmployees.map(e => e.employee_id));
    }
  };

  const handleToggle = (id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleBatchValidate = async () => {
    if (selectedIds.length === 0 || !month) return;
    setValidating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await validationAPI.batchValidate({
        employee_ids: selectedIds,
        year,
        month,
      });
      setSuccess(`Validati ${result.total_success} su ${result.total_processed} cartellini${result.total_failed > 0 ? ` (${result.total_failed} errori)` : ''}`);
      setSelectedIds([]);
      await loadPending();
      onValidated();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Errore validazione batch');
    } finally {
      setValidating(false);
    }
  };

  if (!month) return null;

  const anomalyStatusChip = (status: string, count: number) => {
    if (status === 'clean') return <Chip label="Regolare" color="success" size="small" />;
    if (status === 'minor') return <Chip label={`${count} minori`} color="warning" size="small" />;
    return <Chip label={`${count} significative`} color="error" size="small" />;
  };

  return (
    <Paper sx={{ mt: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckCircleIcon color="primary" />
          <Typography variant="h6">
            Validazione Batch - {monthNames[month]} {year}
          </Typography>
          <Chip label={`${pendingEmployees.length} in attesa`} size="small" variant="outlined" />
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadPending}
            disabled={loading}
          >
            Aggiorna
          </Button>
          {selectedIds.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={validating ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleBatchValidate}
              disabled={validating}
            >
              Valida Selezionati ({selectedIds.length})
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mx: 2, mb: 1 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : pendingEmployees.length === 0 ? (
        <Box textAlign="center" p={4}>
          <Typography color="text.secondary">
            Tutti i cartellini sono stati validati per questo mese.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.length > 0 && !allSelected}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell>Dipendente</TableCell>
                <TableCell>Matricola</TableCell>
                <TableCell align="center">Stato Anomalie</TableCell>
                <TableCell align="center">Validabile</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingEmployees.map((emp) => (
                <TableRow
                  key={emp.employee_id}
                  hover
                  sx={{
                    opacity: emp.can_validate ? 1 : 0.6,
                    backgroundColor: emp.anomaly_status === 'significant' ? 'rgba(244, 67, 54, 0.04)' : 'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(emp.employee_id)}
                      onChange={() => handleToggle(emp.employee_id)}
                      disabled={!emp.can_validate}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {emp.cognome} {emp.nome}
                    </Typography>
                  </TableCell>
                  <TableCell>{emp.matricola || '-'}</TableCell>
                  <TableCell align="center">
                    {anomalyStatusChip(emp.anomaly_status, emp.anomalies_count)}
                  </TableCell>
                  <TableCell align="center">
                    {emp.can_validate ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <Tooltip title="Anomalie significative da risolvere">
                        <WarningIcon color="error" fontSize="small" />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TimesheetManagement;

